<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="handleClose">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/60 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 sm:p-6">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                            <form @submit.prevent="handleSubmit">
                                <div class="px-6 pb-4 pt-6">
                                    <button
                                        type="button"
                                        class="absolute right-5 top-5 text-gray-400 hover:text-gray-600 focus:outline-none"
                                        @click="handleClose"
                                    >
                                        <font-awesome-icon :icon="faXmark" class="h-5 w-5" />
                                        <span class="sr-only">Close</span>
                                    </button>

                                    <div class="pr-10">
                                        <div class="flex items-center gap-3">
                                            <font-awesome-icon
                                                :icon="faShieldCheckSolid"
                                                class="h-7 w-7 shrink-0 text-rent-risk-blue-400"
                                                aria-hidden="true"
                                            />
                                            <DialogTitle as="h2" class="text-2xl font-bold leading-tight text-rent-risk-blue-400">
                                                Verify this Application
                                            </DialogTitle>
                                        </div>
                                        <p class="mt-3 text-sm leading-5 text-gray-600">
                                            Reduce application fraud by requesting information from verified sources, like your applicant's bank account or employer's portal. They'll receive an email with instructions to complete additional steps on their application.
                                        </p>
                                    </div>

                                    <div class="mt-4 border-t border-gray-200">
                                        <div
                                            v-for="row in orderedAddonRows"
                                            :key="row.addon.id"
                                            class="flex items-center gap-3 border-b border-gray-200 py-3"
                                        >
                                            <input
                                                v-if="row.isAssigned"
                                                type="checkbox"
                                                checked
                                                disabled
                                                class="h-4 w-4 shrink-0 self-center rounded border-gray-300 text-gray-400 focus:ring-gray-400"
                                            />
                                            <input
                                                v-else
                                                v-model="selectedAddons"
                                                type="checkbox"
                                                :value="row.addon.id"
                                                class="h-4 w-4 shrink-0 self-center rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                            />

                                            <div
                                                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                                                :class="presentationFor(row.addon).iconBg"
                                            >
                                                <font-awesome-icon
                                                    :icon="presentationFor(row.addon).icon"
                                                    class="h-4 w-4"
                                                    :class="presentationFor(row.addon).iconColor"
                                                    aria-hidden="true"
                                                />
                                            </div>

                                            <div class="min-w-0 flex-1">
                                                <p
                                                    class="text-sm font-bold leading-5"
                                                    :class="row.isAssigned ? 'text-gray-400' : 'text-gray-900'"
                                                >
                                                    {{ row.addon.title }}
                                                </p>
                                                <p
                                                    class="mt-0.5 text-sm leading-5"
                                                    :class="row.isAssigned ? 'text-gray-400' : 'text-gray-500'"
                                                >
                                                    {{ presentationFor(row.addon).description }}
                                                </p>
                                            </div>

                                            <span
                                                class="shrink-0 self-center text-sm font-bold tabular-nums"
                                                :class="row.isAssigned ? 'text-gray-400' : 'text-gray-900'"
                                            >
                                                ${{ formatPrice(row.price) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="mt-4 flex flex-col gap-3 rounded-xl border border-gold-300 bg-gold-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div class="min-w-0">
                                            <div class="flex items-center gap-2">
                                                <font-awesome-icon :icon="faStar" class="h-4 w-4 text-gold-900" aria-hidden="true" />
                                                <p class="text-sm font-bold text-gold-900">RentRisk Recommendation</p>
                                            </div>
                                            <p class="mt-1 text-sm leading-5 text-gray-800">
                                                Bundling all reports for max risk reduction:
                                                <span class="font-bold">{{ recommendationLabel }}</span>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            :disabled="allSelectableSelected"
                                            class="shrink-0 rounded-md border border-gold-900 bg-white px-5 py-2 text-sm font-semibold text-gold-900 shadow-sm transition hover:bg-gold-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            @click="selectAllAddons"
                                        >
                                            Apply Recommended
                                        </button>
                                    </div>

                                    <div class="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3">
                                        <div class="flex items-center gap-3">
                                            <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                                                <font-awesome-icon :icon="faMoneyCheckDollar" class="h-4 w-4 text-gray-500" aria-hidden="true" />
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <p class="text-sm font-bold text-gray-900">Order Summary</p>
                                                <div class="mt-2 space-y-2">
                                                    <div class="flex items-center justify-between text-sm">
                                                        <span class="text-gray-600">Selected Reports</span>
                                                        <span class="font-medium tabular-nums text-gray-900">${{ selectedReportsSubtotal.toFixed(2) }}</span>
                                                    </div>
                                                    <div v-if="isFreeBundleEligible" class="flex items-center justify-between text-sm text-green-700">
                                                        <span class="font-medium">RiskGuard Pro Free Bundle</span>
                                                        <span class="font-semibold">FREE ({{ Math.max(0, 2 - (freeBundlesUsed || 0)) }} remaining)</span>
                                                    </div>
                                                    <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                                        <span class="text-sm font-bold text-gray-900">Estimated Total</span>
                                                        <span class="text-base font-bold tabular-nums text-gray-900">${{ totalAddonPrice.toFixed(2) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- RAC-10337: landlords covering the charge themselves have to
                                         acknowledge it before the reports can be requested. -->
                                    <label v-if="requiresBillingAcknowledgement" class="mt-4 flex items-start gap-3">
                                        <input
                                            v-model="billingAcknowledged"
                                            type="checkbox"
                                            class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                        />
                                        <span class="text-sm leading-5 text-gray-700">
                                            I understand that I am paying for these reports and my payment method on file
                                            will be charged ${{ totalAddonPrice.toFixed(2) }} for this applicant.
                                        </span>
                                    </label>

                                    <p v-if="errorMessage" class="mt-3 text-sm text-red-600">{{ errorMessage }}</p>
                                    <p v-if="paymentMethodSuccess" class="mt-3 text-sm text-green-600">{{ paymentMethodSuccess }}</p>
                                </div>

                                <div class="border-t border-gray-200 px-6 py-4">
                                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <label v-if="showAutoEsoOption" class="flex items-start gap-3 sm:max-w-xl">
                                            <input
                                                v-model="requestAutoEso"
                                                type="checkbox"
                                                class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                            />
                                            <span class="text-sm leading-5 text-gray-700">
                                                Automatically request these reports for all applications to stay Fair Housing compliant
                                                <span class="italic">(recommended)</span>
                                            </span>
                                        </label>
                                        <div v-else class="hidden sm:block sm:flex-1" />

                                        <div class="flex shrink-0 items-center justify-end gap-3">
                                            <button
                                                type="button"
                                                :disabled="isSubmitting"
                                                class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                                                @click="handleClose"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                :disabled="isSubmitting || !canRequestReports"
                                                class="rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Request Reports
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark, faStar, faShieldCheck as faShieldCheckSolid, faBriefcase } from '@fortawesome/pro-solid-svg-icons'
import { faIdCard, faDollarSign, faBuildingColumns, faMoneyCheckDollar } from '@fortawesome/pro-light-svg-icons'

const ADDON_PRESENTATION = {
    identity_verification: {
        description: 'Confirm identity using real time facial recognition and ID upload matching.',
        icon: faIdCard,
        iconBg: 'bg-violet-100',
        iconColor: 'text-violet-700',
        recommendationName: 'Identity',
    },
    income_verification: {
        description: 'Verify income sources through bank account linking.',
        icon: faDollarSign,
        iconBg: 'bg-green-100',
        iconColor: 'text-green-700',
        recommendationName: 'Income',
    },
    payroll: {
        description: 'Confirm current employer, employment status, and income amount.',
        icon: faBriefcase,
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-700',
        recommendationName: 'Employment',
    },
    assets: {
        description: 'View inflow, outflow and ending balance of any linked account.',
        icon: faBuildingColumns,
        iconBg: 'bg-sky-100',
        iconColor: 'text-sky-700',
        recommendationName: 'Assets',
    },
}

export default {
    name: 'EnhancedScreeningOptionsModal',
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        FontAwesomeIcon,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        application: {
            type: Object,
            required: true,
        },
        addons: {
            type: Array,
            required: true,
        },
        hasProtectAccess: {
            type: Boolean,
            default: false,
        },
        subscriptionType: {
            type: String,
            default: null,
        },
        freeBundlesUsed: {
            type: Number,
            default: 0,
        },
        paymentMethodSuccess: {
            type: String,
            default: '',
        },
        freeEsoOfferUsed: {
            type: Boolean,
            default: false,
        },
        showAutoEsoOption: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'submit'],
    data() {
        return {
            faXmark,
            faStar,
            faShieldCheckSolid,
            faMoneyCheckDollar,
            selectedAddons: [],
            selectedReportsSubtotal: 0.00,
            totalAddonPrice: 0.00,
            errorMessage: '',
            isSubmitting: false,
            requestAutoEso: false,
            billingAcknowledged: false,
        }
    },
    computed: {
        orderedAddonRows() {
            const assignedByAddonId = {}

            for (const item of this.application?.required_addons || []) {
                assignedByAddonId[item.addon.id] = item
            }

            return [...this.addons]
                .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
                .map(addon => ({
                    addon,
                    isAssigned: Boolean(assignedByAddonId[addon.id]),
                    price: assignedByAddonId[addon.id]?.price ?? addon.price,
                }))
        },
        selectableAddons() {
            return this.orderedAddonRows.filter(row => !row.isAssigned).map(row => row.addon)
        },
        isDiscountApplied() {
            // Match backend: bundle price only when this purchase selects every addon
            // at once — not when completing a partial set left over from earlier buys.
            return this.selectedAddons.length === this.addons.length && this.addons.length > 0
        },
        isProtectPro() {
            return this.subscriptionType === 'rentrisk-protect-pro'
        },
        hasFreeBundlesAvailable() {
            return this.isProtectPro && this.freeBundlesUsed < 2
        },
        isFreeBundleEligible() {
            return this.hasProtectAccess && this.hasFreeBundlesAvailable && this.selectedAddons.length > 0
        },
        allSelectableSelected() {
            if (this.selectableAddons.length === 0) {
                return true
            }

            return this.selectableAddons.every(addon => this.selectedAddons.includes(addon.id))
        },
        recommendationLabel() {
            return [...this.addons]
                .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
                .map(addon => ADDON_PRESENTATION[addon.unique_name]?.recommendationName)
                .filter(Boolean)
                .join(' + ')
        },
        requiresBillingAcknowledgement() {
            return this.selectedAddons.length > 0 && this.totalAddonPrice > 0
        },
        canRequestReports() {
            if (this.selectedAddons.length === 0) {
                return false
            }

            return !this.requiresBillingAcknowledgement || this.billingAcknowledged
        },
    },
    watch: {
        show: {
            handler(newVal) {
                if (newVal) {
                    this.selectedAddons = []
                    this.selectedReportsSubtotal = 0
                    this.totalAddonPrice = 0
                    this.errorMessage = ''
                    this.isSubmitting = false
                    this.requestAutoEso = false
                    this.billingAcknowledged = false
                }
            },
            immediate: true,
        },
        selectedAddons: {
            handler() {
                this.recalculatePricing()
            },
            immediate: true,
            deep: true,
        },
        hasProtectAccess(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.recalculatePricing()
                })
            }
        },
    },
    methods: {
        presentationFor(addon) {
            return ADDON_PRESENTATION[addon?.unique_name] || {
                description: addon?.title || '',
                icon: faShieldCheckSolid,
                iconBg: 'bg-gray-100',
                iconColor: 'text-gray-500',
                recommendationName: addon?.title,
            }
        },
        formatPrice(price) {
            return Number.parseFloat(price).toFixed(2)
        },
        recalculatePricing() {
            if (this.selectedAddons.length > 0) {
                this.errorMessage = ''
            }

            const paidAddonIds = this.application?.required_addons?.map(item => item.addon_id) || []
            let subtotal = 0

            this.selectedAddons.forEach(selectedId => {
                if (paidAddonIds.includes(selectedId)) {
                    return
                }

                const addon = this.addons.find(item => item.id === selectedId)
                if (addon) {
                    subtotal += Number.parseFloat(addon.price)
                }
            })

            this.selectedReportsSubtotal = Number.isNaN(subtotal) ? 0 : subtotal

            let total = this.selectedReportsSubtotal

            if (this.isFreeBundleEligible) {
                total = 0
            } else if (this.isDiscountApplied) {
                total = Number.parseFloat(import.meta.env.VITE_ADDONS_DISCOUNTED_PRICE_TOTAL ?? '29')
            }

            this.totalAddonPrice = Number.isNaN(total) ? 0 : total
        },
        handleSubmit() {
            this.errorMessage = ''

            if (this.selectedAddons.length === 0 && (!this.application?.required_addons || this.application.required_addons.length === 0)) {
                this.errorMessage = 'Please select at least one addon to proceed.'
                return false
            }

            if (this.requiresBillingAcknowledgement && !this.billingAcknowledged) {
                this.errorMessage = 'Please acknowledge that you are paying for these reports to proceed.'
                return false
            }

            this.isSubmitting = true
            this.$emit('submit', {
                addons: this.selectedAddons,
                totalPrice: this.totalAddonPrice,
                requestAutoEso: this.requestAutoEso,
            })
        },
        handleClose() {
            this.selectedAddons = []
            this.errorMessage = ''
            this.isSubmitting = false
            this.requestAutoEso = false
            this.billingAcknowledged = false
            this.$emit('close')
        },
        resetSubmissionState() {
            this.isSubmitting = false
        },
        selectAllAddons() {
            const paidAddonIds = this.application?.required_addons?.map(item => item.addon_id) || []
            this.selectedAddons = this.addons
                .filter(addon => !paidAddonIds.includes(addon.id))
                .map(addon => addon.id)
        },
    },
}
</script>
